'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { CAPABILITIES, hasCapability } from '@utils/capabilities';
import type { OrganizationUserRole } from '@ent/permissions-shared';
import { Plus } from 'lucide-react';
import { AiIcon } from '@components/ui/ai-icon';
import { Pressable } from '@components/mobile/controls/pressable';
import AppAddCreateButton from '@components/AppAddCreateButton';
import { useAgentsMembershipEnabled } from '@hooks/use-agents-membership';
import { useStartAgentChat } from '@hooks/use-start-agent-chat';
import { getHref, ROUTE } from '@utils/route-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';
import { useCurrentOrgUser } from '../../../zero/use-current-user';

const CreateChannelDialog = dynamic(
  () => import('@components/CreateChannelDialog')
);
const CHROME_CIRCLE =
  'bg-card inline-flex size-11 shrink-0 items-center justify-center rounded-full text-foreground';

export function HeaderActions({
  organization_id,
  surface = 'mobile'
}: {
  organization_id: string;
  surface?: 'mobile' | 'desktop';
}) {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const { chrome } = usePageChrome();
  const orgUser = useCurrentOrgUser(organization_id);
  const [composing, setComposing] = useState(false);
  const agentsEnabled = useAgentsMembershipEnabled();
  const { start, loading } = useStartAgentChat(organization_id);
  const isShift = pathname === getHref(organization_id, [ROUTE.MESSAGES]);

  return (
    <div className="flex items-center gap-1.5">
      {agentsEnabled ? (
        <Pressable
          feedback="scale"
          aria-label={t('common.ai-chat')}
          onClick={() => void start()}
          disabled={loading}
          className={CHROME_CIRCLE}
        >
          <AiIcon className="size-5" />
        </Pressable>
      ) : null}
      {isShift &&
      hasCapability(
        orgUser?.role as OrganizationUserRole | undefined,
        CAPABILITIES.SendMessages
      ) &&
      !chrome?.hideMobileTabs ? (
        <>
          <Pressable
            feedback="scale"
            aria-label={t('navigation.new-conversation')}
            onClick={() => setComposing(true)}
            className={CHROME_CIRCLE}
          >
            <Plus className="size-5" aria-hidden />
          </Pressable>
          {composing ? (
            <CreateChannelDialog
              organization_id={organization_id}
              directMessage
              open
              onOpenChange={setComposing}
              onCreated={id =>
                router.push(getHref(organization_id, [ROUTE.MESSAGES, id]))
              }
            />
          ) : null}
        </>
      ) : surface !== 'desktop' || !chrome?.hideDesktopCreateAction ? (
        <AppAddCreateButton
          organization_id={organization_id}
          variant="chrome"
        />
      ) : null}
    </div>
  );
}
